import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import {
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Content from 'components/content/content'
import Dialog from 'components/dialog/dialog'
import Filter from 'components/filter/filter'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import useDebounce from 'modules/useDebounce'
import type { ErrorProps } from 'modules/types'
import type { ProductInquiry, PartialProductInquiry } from 'modules/productinquiry/product-inquiry.dto'
import Notification from 'components/notification'
import {
  useDownloadProductInquiryListMutation,
  useGetDcxtRefIdListMutation,
  useGetProductInquiryListMutation,
  useSendProductInquiryToConnexMutation
} from 'store/productInquiry'

const GetAnOfferList = () => {
  const [getList, list] = useGetProductInquiryListMutation()
  const [downloadList, download] = useDownloadProductInquiryListMutation()
  const [getDcxtRefIdList, dcxtRefIdList] = useGetDcxtRefIdListMutation()
  const [sendToConnex, sync] = useSendProductInquiryToConnexMutation()

  const [dialogSync, setDialogSync] = useState<boolean>(false)

  const [filter, setFilter] = useState({
    minSubmissionDate: '',
    maxSubmissionDate: '',
    sentDcxt: '',
    dcxtRefId: ''
  })

  const [dcxtRefIdSearch, setDcxtRefIdSearch] = useState<string>('')
  const [dcxtRefIdFocus, setDcxtRefIdFocus] = useState<boolean>(false)
  const debouncedDcxtRefIdSearch = useDebounce(dcxtRefIdSearch)

  const [payload, setPayload] = useState<PartialProductInquiry>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    search: '',
    minSubmissionDate: '',
    maxSubmissionDate: '',
    sentDcxt: '',
    dcxtRefId: ''
  })

  const [dialogFilter, setDialogFilter] = useState<boolean>(false)

  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value })
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })
  const onFilter = (state: boolean) => setDialogFilter(state)

  useEffect(() => {
    if (dcxtRefIdFocus) getDcxtRefIdList({ search: debouncedDcxtRefIdSearch || '' })
  }, [debouncedDcxtRefIdSearch, dcxtRefIdFocus])

  const buildFormData = (length: number) => {
    const formData = new FormData()

    formData.append('start', String(payload.start))
    formData.append('length', String(length))
    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)
    if (!isEmpty(payload.minSubmissionDate))
      formData.append('MinSubmissionDate', payload.minSubmissionDate)
    if (!isEmpty(payload.maxSubmissionDate))
      formData.append('MaxSubmissionDate', payload.maxSubmissionDate)
    if (!isEmpty(payload.sentDcxt)) formData.append('SentDcxt', payload.sentDcxt)
    if (!isEmpty(payload.dcxtRefId)) formData.append('DcxtRefId', payload.dcxtRefId)

    return formData
  }

  const onResync = () => getList(buildFormData(payload.length))

  const onDownload = (type?: string) => {
    const formData = buildFormData(CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD)
    formData.set('start', String(0))
    if (type && !isEmpty(type)) formData.append('Download-Type', type)

    downloadList(formData)
  }

  const columns = [
    { column: 'File Ref ID', label: 'File Ref ID', key: 'dcxtRefId' },
    { column: 'Member Code', label: 'Member Code', key: 'memberCode' },
    { column: 'Customer Name', label: 'Customer Name', key: 'customerName' },
    { column: 'Customer Phone', label: 'Customer Phone', key: 'customerPhone' },
    { column: 'Referral Member ID', label: 'Referral Member ID', key: 'referralMemberCode' },
    { column: 'Referral Phone Number', label: 'Referral Phone Number', key: 'referralMemberPhone' },
    { column: 'Car Model Name', label: 'Car Model Name', key: 'carModelName' },
    { column: 'Province', label: 'Province', key: 'province' },
    { column: 'City', label: 'City', key: 'city' },
    {
      column: 'Consent',
      label: 'Consent',
      key: 'consentAccepted',
      render: (row: ProductInquiry) => (
        <Chip label={row.consentAccepted ? 'Yes' : 'No'} color={row.consentAccepted ? 'success' : 'default'} />
      )
    },
    {
      column: 'Form Submission Date',
      label: 'Form Submission Date',
      key: 'createdAt',
      render: (row: ProductInquiry) => (row.createdAt ? dayjs(row.createdAt).format('DD-MM-YYYY HH:mm') : '-')
    },
    {
      column: 'External Sent',
      label: 'External Sent',
      key: 'sentDcxt',
      render: (row: ProductInquiry) => (
        <Chip label={row.sentDcxt ? 'Yes' : 'No'} color={row.sentDcxt ? 'success' : 'error'} />
      )
    }
  ]

  const tableHeadTitles = columns.map((c) => c.label)

  const totalPage = list.data && GLOBAL.tableTotalPage(list.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Get an Offer'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onFilter={() => onFilter(true)}
        onSync={() => setDialogSync(true)}
        syncTitle='Sync'
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        onDownloadTypeIgnored={['pdf']}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={list.data && list.data.recordsTotal}
        page={payload.start + 1}
        isLoading={list.isLoading}
        useAction={false}
        useDownloadDropdown
      >
        {list.isSuccess &&
          list.data &&
          list.data.data?.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render ? col.render(table) : table[col.key as keyof ProductInquiry] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      <Filter
        open={dialogFilter}
        title='Filter'
        isSubmitDisabled={!GLOBAL.isFilterContainValue(filter)}
        onClose={() => onFilter(false)}
        onCancel={() => onFilter(false)}
        onReset={() => {
          setFilter({ minSubmissionDate: '', maxSubmissionDate: '', sentDcxt: '', dcxtRefId: '' })
          setPayload({
            ...payload,
            start: 0,
            minSubmissionDate: '',
            maxSubmissionDate: '',
            sentDcxt: '',
            dcxtRefId: ''
          })
          onFilter(false)
        }}
        onSubmit={() => {
          setPayload({ ...payload, start: 0, ...filter })
          onFilter(false)
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
          <DatePicker
            value={filter.minSubmissionDate ? dayjs(filter.minSubmissionDate) : null}
            maxDate={filter.maxSubmissionDate ? dayjs(filter.maxSubmissionDate) : undefined}
            onChange={(date: Dayjs | null) =>
              date && setFilter({ ...filter, minSubmissionDate: date.format('YYYY-MM-DD') })
            }
            label='Submission Start'
            format='DD-MM-YYYY'
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
          <DatePicker
            value={filter.maxSubmissionDate ? dayjs(filter.maxSubmissionDate) : null}
            minDate={filter.minSubmissionDate ? dayjs(filter.minSubmissionDate) : undefined}
            onChange={(date: Dayjs | null) =>
              date && setFilter({ ...filter, maxSubmissionDate: date.format('YYYY-MM-DD') })
            }
            label='Submission End'
            format='DD-MM-YYYY'
          />
        </LocalizationProvider>
        <FormControl fullWidth>
          <InputLabel id='sent-dcxt-label'>Status sent to Innocean</InputLabel>
          <Select
            labelId='sent-dcxt-label'
            id='sent-dcxt-select'
            value={filter.sentDcxt}
            label='Status sent to Innocean'
            onChange={(event) => setFilter({ ...filter, sentDcxt: event.target.value })}
          >
            <MenuItem value='true'>Sent</MenuItem>
            <MenuItem value='false'>Not Sent</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          options={dcxtRefIdList.data || []}
          value={filter.dcxtRefId || null}
          onChange={(_, value) => setFilter({ ...filter, dcxtRefId: value || '' })}
          onInputChange={(_, value) => setDcxtRefIdSearch(value)}
          onFocus={() => setDcxtRefIdFocus(true)}
          renderInput={(params) => <TextField {...params} label='File Ref ID' />}
        />
      </Filter>

      <Dialog
        title='Sync Item'
        submitText='Yes, Sure'
        open={dialogSync}
        onCancel={() => setDialogSync(false)}
        onSubmit={() => sendToConnex()}
        loading={sync.isLoading}
      >
        <Typography>Do you want to proceed with syncing the data to Innocean?</Typography>
      </Dialog>

      <Notification
        open={!sync.isLoading && !sync.isUninitialized}
        onClose={() => {
          if (sync.isError) sync.reset()
          else {
            setDialogSync(false)
            location.reload()
          }
        }}
        isError={Boolean(sync.error) && sync.isError}
        message={
          sync.isError
            ? GLOBAL.returnExceptionMessage(sync.isError, sync.error as ErrorProps)
            : 'Sync is now running in the background and may take several minutes to finish.'
        }
      />

      <Notification
        open={list.isError}
        onClose={() => list.reset()}
        isError={list.isError}
        message={GLOBAL.returnExceptionMessage(list.isError, list.error as ErrorProps)}
      />

      <Notification
        open={!download.isLoading && !download.isUninitialized && !download.isSuccess}
        onClose={() => download.reset()}
        isError={Boolean(download.error) && download.isError}
        message={GLOBAL.returnExceptionMessage(download.isError, download.error as ErrorProps)}
      />
    </>
  )
}

export default GetAnOfferList
